import { WaterIndexModel } from "@/core/models/water-index.model";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import dayjs from "dayjs";
import { DifferenceCell } from "../../components/DifferenceCell";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { WaterIndexRepo } from "@/core/repos/tables/water-index.repo";
import { UserRepo } from "@/core/repos/user.repo";
import container from "@/lib/di/container";
import { FilterInputType, FilterInputTemplate } from "@/app/home/components/filter/FilterBar";
import { ReportTableConfigs } from "@/app/home/components/report/ReportTable";
import { GlobalUtils } from "@/app/home/utils/global.utils";
import { Button, Image, Modal } from "antd";
import { FileImageOutlined, PictureOutlined } from "@ant-design/icons";

const waterIndexRepo = container.get(WaterIndexRepo);
const tollAreaRepo = container.get(TollAreaRepo);
const userRepo = container.get(UserRepo);

export const showIndexImageModal = (src: string) => {
    Modal.info({
        title: "Hình ảnh chỉ số nước",
        width: 800,
        content: (
            <div className="flex justify-center">
                <Image src={src} alt="Chỉ số nước" className="max-w-full max-h-[500px] object-contain" />
            </div>
        ),
        okText: "Đóng",
    });
}

export const WATER_INDEX_REPORT_CONFIGS: {name: string, props: ReportTableConfigs<WaterIndexModel, null>} = {
    name: "Báo cáo Chỉ số nước",
    props: {
        initialFilterMap: new Map(
            Object.entries({...GlobalUtils.getPreviousMonthFilter()})
        ),
        columns: [
            { name: 'Mã hợp đồng', pipe: (value: WaterIndexModel) => value.waterUser?.code??"" },
            { name: 'Đại diện hợp đồng', pipe: (value: WaterIndexModel) => value.waterUser?.name??"" },
            { name: 'Mã đồng hồ nước', pipe: (value: WaterIndexModel) => value?.waterMeterCode??""},
            { name: 'Khu vực', pipe: (value: WaterIndexModel) => value.tollArea?.name??"" },
            { dataIndex: 'waterMeterNumber', name: 'Số đồng hồ' },
            { dataIndex: 'oldNumber', name: 'Số đo cũ' },
            { dataIndex: 'useAmount', name: 'Số lượng sử dụng' },
            { dataIndex: 'differenceRatio', name: 'Chênh lệch', render: (percent) => percent?<DifferenceCell percent={percent} />:null},
            { dataIndex: 'createdAt', name: 'Ngày đo', pipe: formatDateTimePipe },
            { dataIndex: 'createdBy', name: 'Người đo' },
            { name: 'Ảnh', fixed: "right", pipe: (data: WaterIndexModel) => {
                return (
                    <Button type="text" disabled={!data.image} onClick={() => data.image && showIndexImageModal(data.image)} icon={<PictureOutlined />}/>
                );
            }}
        ],
        filterTemplates:[
            {
                dataIndex: "search",
                label: "Tìm kiếm",
                placeholder: "Nhập nội dung tìm kiếm",
                type: FilterInputType.text,
            },
            {
                dataIndex: "month",
                label: "Tháng",
                placeholder: "Chọn tháng",
                type: FilterInputType.month,
            },
            {
                dataIndex: "tollAreaId",
                label: "Khu vực",
                placeholder: "Chọn Khu vực",
                type: FilterInputType.select,
                options: []
            },
            {
                dataIndex: "status",
                label: "Trạng thái đọc",
                placeholder: "Chọn trạng thái đọc",
                type: FilterInputType.select,
                options: [
                    {value: null, name: "Tất cả"},
                    {value: "read", name: "Đã đọc"},
                    {value: "unread", name: " Chưa đọc"},
                ]
            },
            {
                dataIndex: "useAmountWaterIndex",
                label: "Lượng tiêu thụ",
                placeholder: "Nhập lượng tiêu thụ",
                type: FilterInputType.number,
                disabled: true
            },
            {
                dataIndex: "differenceRatio",
                label: "Biến động % tối thiểu",
                placeholder: "Nhập % biến động cần tra cứu",
                type: FilterInputType.number,
                disabled: true
            },
            {
                dataIndex: "createdBy",
                label: "Người đọc",
                placeholder: "Chọn Người đọc",
                type: FilterInputType.select,
                options: []
            },
        ],
        handleExport: async (filterMap, defaultFilter) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const query: any = {...defaultFilter};
            filterMap.forEach(filter => {
                if (filter.value) {
                    query[filter.key] = filter.value
                }
            })
            const blobData = await waterIndexRepo.exportExcel(query);
            if (blobData) {
                GlobalUtils.downloadXLSX(blobData, `DanhSachChiSoNuoc-${dayjs().format("DDMMYYYY-hhmm")}`)
            };
        },
        init: async (filterTemplates) => {
            const tollAreas = (await tollAreaRepo.getAll()).map(r => ({name: r.name, value: r.id}));
            const users = (await userRepo.getFromFactory()).map(r => ({name: `${r.fullName}-${r.userName.toUpperCase()}`, value: r.userName}))
            const templates: FilterInputTemplate[] = [...filterTemplates];
            const tollAreaTemplate = templates.find(template => template.dataIndex === "tollAreaId");
            if (tollAreaTemplate) {
                tollAreaTemplate.options = tollAreas;
            }
            const userTemplate = templates.find(template => template.dataIndex === "createdBy");
            if (userTemplate) {
                userTemplate.options = users;
            }
            return {
                filterTemplates: templates
            }
        },
        onFilterChanged: async (map, changes, filterTemplates) => {
            let isDirty = false;
            const templates = [...filterTemplates]
    
            const status = map.get("status")?.value;
            const disabled = status != "read";
    
            const useAmountWaterIndexTemplate = templates.find(t => t.dataIndex === "useAmountWaterIndex");
            if (useAmountWaterIndexTemplate) {
                useAmountWaterIndexTemplate.disabled = disabled;
                isDirty = true;
            }
    
            const differenceRatioTemplate = templates.find(t => t.dataIndex === "differenceRatio");
            if (differenceRatioTemplate) {
                differenceRatioTemplate.disabled = disabled;
                isDirty = true;
            }
    
            if (disabled) {
                map.delete("useAmountWaterIndex");
                map.delete("differenceRatio");
            }
    
            if (isDirty) {
                return {
                    filterTemplates: templates,
                    filterMap: map
                }
            }
            return {
                filterTemplates: undefined
            }
        },
        repository: waterIndexRepo
    }
}